import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Users, GraduationCap, Briefcase, Building2, CreditCard, BookOpen, ClipboardCheck, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const ServicesPage = () => {
  const services = [
    {
      icon: <FileText className="h-8 w-8 text-blue-600" />,
      title: 'Personal Tax Returns',
      description: 'Complete tax preparation and filing for Canadian residents',
      features: [
        'T1 personal tax returns',
        'Employment income (T4 slips)',
        'Investment income reporting',
        'RRSP contributions & deductions',
        'Medical expenses & credits',
        'Home office deductions',
      ],
      benefits: 'Get every dollar you deserve back. We identify all eligible credits and deductions to maximize your refund while ensuring CRA compliance.',
    },
    {
      icon: <Users className="h-8 w-8 text-blue-600" />,
      title: 'Newcomer & First-Time Filer Services',
      description: 'Specialized tax help for new Canadians and first-time filers',
      features: [
        'First-time tax filer assistance',
        'Newcomer tax credits & benefits',
        'Foreign income reporting',
        'Tax treaty benefits',
        'Provincial residency determination',
        'Immigration tax planning',
      ],
      benefits: 'Navigate Canadian tax system with confidence. We help newcomers understand their obligations and claim all available benefits from day one.',
    },
    {
      icon: <GraduationCap className="h-8 w-8 text-blue-600" />,
      title: 'Student Tax Returns',
      description: 'Tax services tailored for college and university students',
      features: [
        'Tuition and education credits',
        'Textbook and supply deductions',
        'Student loan interest claims',
        'Part-time employment income',
        'Scholarship and bursary reporting',
        'Moving expense deductions',
      ],
      benefits: 'Maximize your student refunds and carry forward unused credits. We help students get money back for education expenses and plan for future years.',
    },
    {
      icon: <Briefcase className="h-8 w-8 text-blue-600" />,
      title: 'Self-Employed Tax Filing',
      description: 'Tax solutions for freelancers, contractors, and gig workers',
      features: [
        'Business income & expense tracking',
        'GST/HST filing & remittance',
        'Home office expense claims',
        'Vehicle and travel deductions',
        'CPP contributions calculation',
        'Quarterly tax planning',
      ],
      benefits: 'Keep more of what you earn. We maximize legitimate business deductions while keeping you compliant with CRA requirements.',
    },
    {
      icon: <Building2 className="h-8 w-8 text-blue-600" />,
      title: 'Corporation Taxes',
      description: 'Complete corporate tax preparation and filing services',
      features: [
        'T2 corporate tax returns',
        'Small business deduction optimization',
        'Corporate income tax planning',
        'Year-end tax strategies',
        'Shareholder benefits reporting',
        'Corporate tax compliance',
      ],
      benefits: 'Minimize corporate tax liability while staying fully compliant. Expert guidance on tax-efficient business structures and strategies.',
    },
    {
      icon: <CreditCard className="h-8 w-8 text-blue-600" />,
      title: 'Payroll Services',
      description: 'Professional payroll management for businesses',
      features: [
        'Payroll processing & payments',
        'T4 and ROE preparation',
        'Source deduction remittance',
        'WSIB reporting & compliance',
        'Employee pay stubs',
        'Year-end payroll reconciliation',
      ],
      benefits: 'Ensure employees are paid accurately and on time. We handle all payroll compliance so you can focus on growing your business.',
    },
    {
      icon: <BookOpen className="h-8 w-8 text-blue-600" />,
      title: 'Bookkeeping Services',
      description: 'Professional bookkeeping to keep your finances organized',
      features: [
        'Daily transaction recording',
        'Bank reconciliation',
        'Accounts payable/receivable',
        'Financial statement preparation',
        'Monthly/quarterly reporting',
        'Cloud-based accounting setup',
      ],
      benefits: 'Stay on top of your finances with accurate, up-to-date bookkeeping. Make informed business decisions with clear financial insights.',
    },
    {
      icon: <ClipboardCheck className="h-8 w-8 text-blue-600" />,
      title: 'Business Registration',
      description: 'Complete business setup and registration services',
      features: [
        'Business name registration',
        'Federal incorporation',
        'Provincial registration',
        'GST/HST registration',
        'Business number acquisition',
        'CRA account setup',
      ],
      benefits: 'Start your business right with proper registration. We guide you through every step to ensure legal compliance from day one.',
    },
    {
      icon: <HelpCircle className="h-8 w-8 text-blue-600" />,
      title: 'CRA Support & Assistance',
      description: 'Help with CRA notices, audits, and tax issues',
      features: [
        'CRA notice response',
        'Tax debt resolution',
        'Audit representation',
        'Taxpayer relief applications',
        'Payment arrangement setup',
        'Penalty & interest reduction',
      ],
      benefits: 'Don\'t face the CRA alone. We provide expert representation and help resolve tax issues quickly and professionally.',
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Our Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professional tax preparation services designed for individuals at every stage of life. 
            Whether you're a student, working professional, self-employed, or new to Canada, our expert team is here to assist you.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="border-2 border-blue-100 shadow-lg hover:shadow-xl transition-shadow" data-testid={`service-card-${index}`}>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      {service.icon}
                    </div>
                    <CardTitle className="text-xl text-gray-900">{service.title}</CardTitle>
                  </div>
                  <CardDescription className="text-base mt-1">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">What We Offer:</h4>
                    <ul className="space-y-1">
                      {service.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-sm">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                      {service.features.length > 4 && (
                        <li className="text-sm text-blue-600">+ {service.features.length - 4} more</li>
                      )}
                    </ul>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">{service.benefits}</p>
                  </div>
                  <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                    <Link to="/contact">
                      Get Started <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Not Sure What You Need?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Book a free consultation and we'll help you understand your tax situation and maximize your refund
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6">
              <Link to="/contact">
                Schedule Consultation
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-blue-700 text-lg px-8 py-6">
              <a href="https://wa.me/message/E7OKL6FXEAG6L1" target="_blank" rel="noopener noreferrer">
                Chat on WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Professional, Reliable, Trusted
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto mb-8">
              All our services come with the ManoMay guarantee: accurate work, timely delivery, 
              and complete confidentiality. Your financial success is our priority.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-blue-600">5000+</p>
                <p className="text-gray-600">Clients Helped</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-blue-600">24-48hr</p>
                <p className="text-gray-600">Response Time</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-blue-600">100%</p>
                <p className="text-gray-600">Confidential</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
